// frontend/src/utils/dayShiftPatterns.js
// 「人員配置モードで使用する日勤」パターンの取得ヘルパー
//
// 旧来は monthly_shifts.shift_type が空 / "◯" を「日勤」とみなしていたが、
// 職種別日勤マーク（例: ◯N）の導入後は shift_patterns.configuration.assignment_mode_enabled
// が ON のパターンの short_name 群を「日勤候補」として扱う。
//
// このヘルパーは組織単位で許可された日勤マークを取得し、各画面で共通に使う。

import { supabase } from '../supabase';

const cache = new Map(); // orgId -> { marks: Set<string>, fetchedAt: number }
const CACHE_TTL_MS = 60 * 1000;

/**
 * 組織で「人員配置モードで使用する」とマークされた日勤パターンの short_name (displayMark) を返す。
 * @param {string} organizationId
 * @returns {Promise<Set<string>>}
 */
export async function fetchOrgDayShiftMarks(organizationId, { force = false } = {}) {
  if (!organizationId) return new Set();
  const cached = cache.get(organizationId);
  if (!force && cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
    return cached.marks;
  }

  const { data, error } = await supabase
    .from('shift_patterns')
    .select('short_name, configuration')
    .eq('organization_id', organizationId)
    .eq('is_active', true);

  if (error) {
    console.error('❌ shift_patterns 取得エラー (dayShiftPatterns):', error);
    return new Set();
  }

  const marks = new Set();
  (data || []).forEach((p) => {
    const enabled =
      p?.configuration?.assignmentModeEnabled === true
      || p?.configuration?.assignment_mode_enabled === true;
    const mark = p?.short_name;
    if (enabled && mark) marks.add(String(mark));
  });

  cache.set(organizationId, { marks, fetchedAt: Date.now() });
  return marks;
}

/**
 * 指定 shift_type が「人員配置モードで使用する日勤」に該当するかを返す。
 * @param {string|null|undefined} shiftType
 * @param {Set<string>} marks
 */
export function isAssignmentModeDayShift(shiftType, marks) {
  if (!shiftType) return false;
  if (!(marks instanceof Set) || marks.size === 0) return false;
  return marks.has(String(shiftType));
}

/**
 * キャッシュを破棄（パターン編集後に呼ぶ）
 */
export function invalidateDayShiftCache(organizationId) {
  if (organizationId) cache.delete(organizationId);
  else cache.clear();
}
